/**
 * A coarse heartbeat that survives background-tab throttling (C7).
 *
 * A backgrounded tab has `requestAnimationFrame` suspended entirely and
 * `setTimeout` clamped to as little as once per minute. Worker timers are
 * throttled far less aggressively, so the decision "should the next transition
 * be armed?" runs there. The worker does no audio work and holds no state — it
 * only says "look again now".
 */
const WORKER_SOURCE = `
let timer = null;
self.onmessage = (event) => {
  const data = event.data || {};
  if (data.type === "start") {
    if (timer !== null) clearInterval(timer);
    timer = setInterval(() => self.postMessage({ type: "tick" }), data.interval);
  } else if (data.type === "stop") {
    if (timer !== null) clearInterval(timer);
    timer = null;
  }
};
`;

export class Ticker {
  private worker: Worker | null = null;
  private fallback: number | undefined;

  constructor(
    private readonly intervalMs: number,
    private readonly onTick: () => void,
  ) {}

  start(): void {
    if (this.worker || this.fallback !== undefined) return;

    try {
      const blob = new Blob([WORKER_SOURCE], { type: "application/javascript" });
      const url = URL.createObjectURL(blob);
      const worker = new Worker(url);
      URL.revokeObjectURL(url);
      worker.onmessage = () => this.onTick();
      worker.postMessage({ type: "start", interval: this.intervalMs });
      this.worker = worker;
    } catch {
      // No workers available: fall back to a window timer. Auto-mix still runs,
      // it is only less reliable while the tab is in the background.
      this.fallback = window.setInterval(() => this.onTick(), this.intervalMs);
    }
  }

  stop(): void {
    if (this.worker) {
      this.worker.postMessage({ type: "stop" });
      this.worker.terminate();
      this.worker = null;
    }
    if (this.fallback !== undefined) {
      window.clearInterval(this.fallback);
      this.fallback = undefined;
    }
  }

  get usingWorker(): boolean {
    return this.worker !== null;
  }
}
